import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="movement-rows"
export default class extends Controller {
  static targets = [
    "container",
    "row",
    "dateField",
    "exclusionReason",
    "exclusionWarning",
  ]

  static values = {
    originInventories: Object,
    originInventoriesBeforeDay: Object,
    labelEod: String,
    labelBefore: String,
    // CML: zone_id (string) → zone_group_id (string) mapping
    zoneGroupByZone: Object,
    // CML: array of [source_zone_group_id, destination_zone_group_id] string pairs that require CML
    requiresCmlZoneGroupPairs: Array,
  }

  connect() {
    this.boundHandleFormQuantitiesChange = this.handleFormQuantitiesChange.bind(this)
    if (this.hasContainerTarget) {
      this.containerTarget.addEventListener("input", this.boundHandleFormQuantitiesChange)
      this.containerTarget.addEventListener("change", this.boundHandleFormQuantitiesChange)
    }
    this.updateAllOriginAvailabilityLabels()
    this.updateAllCmlVisibility()
  }

  disconnect() {
    if (this.hasContainerTarget && this.boundHandleFormQuantitiesChange) {
      this.containerTarget.removeEventListener("input", this.boundHandleFormQuantitiesChange)
      this.containerTarget.removeEventListener("change", this.boundHandleFormQuantitiesChange)
    }
  }

  handleFormQuantitiesChange(event) {
    const t = event.target
    const nm = t.getAttribute?.("name") || ""
    if (!nm.includes("[input_value]") && !nm.includes("[input_type]") && !nm.includes("[origin_zone_id]")) {
      // Still update CML visibility when destination zone changes
      if (nm.includes("[destination_zone_id]")) {
        const row = t.closest("[data-movement-rows-target='row']") || t.closest(".movement-row")
        if (row) this.updateCmlVisibility(row)
      }
      return
    }
    const card = t.closest(".movement-group-card")
    if (card) this.updateRunningAvailabilityForGroup(card)
  }

  redirectToDate(event) {
    const dateField = event.currentTarget
    const selectedDate = dateField.value
    
    if (!selectedDate) {
      return
    }
    
    // Build the URL: /movements/register_day/:date
    // We support both:
    // - /movements/register_day/:current_date
    // - /movements/new_day
    //
    // In both cases, we want to end up on /movements/register_day/:selectedDate
    const currentPath = window.location.pathname
    let basePath = "/movements/register_day/"

    const match = currentPath.match(/^(.*\/movements)\/(register_day\/[^/]+|new_day)$/)
    if (match) {
      basePath = `${match[1]}/register_day/`
    }

    const newUrl = `${basePath}${selectedDate}`
    window.location.href = newUrl
  }

  addRow(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const button = event.currentTarget
    const sourceZoneGroupId = button.dataset.sourceZoneGroupId
    
    // Parse zones data with error handling
    let originZones = []
    let destinationZones = []
    try {
      originZones = JSON.parse(button.dataset.originZones || '[]')
      destinationZones = JSON.parse(button.dataset.destinationZones || '[]')
    } catch (error) {
      console.error('Failed to parse zones data', error)
      return
    }
    
    // Find the group card container
    const card = button.closest('.movement-group-card')
    if (!card) {
      console.error('Could not find movement group card')
      return
    }
    
    // Find the rows container within the card
    const rowsContainer = card.querySelector('.movement-rows-container')
    if (!rowsContainer) {
      console.error('Could not find movement rows container')
      return
    }
    
    // Find the template row (hidden)
    const templateContainer = card.querySelector('.movement-row-template')
    if (!templateContainer) {
      console.error('Could not find movement row template')
      return
    }
    
    const templateRow = templateContainer.querySelector('.movement-row')
    if (!templateRow) {
      console.error('Could not find movement row in template')
      return
    }
    
    // Clone the template row
    const newRow = templateRow.cloneNode(true)
    
    // Find the next available index by counting all existing rows in the main container
    const container = this.containerTarget
    const existingRows = container.querySelectorAll(".movement-row:not(.movement-row-template .movement-row)")
    const newIndex = existingRows.length
    
    // Update all field names with new index
    this.updateFieldNames(newRow, newIndex)
    
    // Update data-movement-index attribute on the row
    newRow.setAttribute('data-movement-index', newIndex)
    
    // Update data-movement-index on the Verifica button if present
    const verificaButton = newRow.querySelector('button[data-action*="validateRow"]')
    if (verificaButton) {
      verificaButton.setAttribute('data-movement-index', newIndex)
    }
    
    // Populate origin zone select with zones from the group
    const originSelect = newRow.querySelector('select[name*="[origin_zone_id]"]')
    if (originSelect) {
      originSelect.innerHTML = ''
      
      originZones.forEach(zone => {
        const option = document.createElement('option')
        option.value = zone.id
        option.textContent = zone.name
        if (zone.inventory !== undefined && zone.inventory !== null) {
          option.dataset.inventory = zone.inventory
        }
        if (zone.inventory_before_day !== undefined && zone.inventory_before_day !== null) {
          option.dataset.inventoryBeforeDay = zone.inventory_before_day
        }
        originSelect.appendChild(option)
      })
      
      // If only one zone, select it; otherwise leave empty
      if (originZones.length === 1) {
        originSelect.value = originZones[0].id
      } else if (originZones.length > 0) {
        // Select first zone as default
        originSelect.value = originZones[0].id
      }

      // Attach change handler so availability updates when origin changes
      originSelect.addEventListener("change", (event) => this.handleOriginChange(event))
    }
    
    // Populate destination zone select with allowed zones (empty with prompt)
    const destinationSelect = newRow.querySelector('select[name*="[destination_zone_id]"]')
    if (destinationSelect) {
      destinationSelect.innerHTML = ''
      
      // Add prompt option
      const promptOption = document.createElement('option')
      promptOption.value = ''
      promptOption.textContent = 'Seleziona destinazione'
      destinationSelect.appendChild(promptOption)
      
      // Add destination options
      destinationZones.forEach(zone => {
        const option = document.createElement('option')
        option.value = zone.id
        option.textContent = zone.name
        destinationSelect.appendChild(option)
      })
      
      // Reset to prompt option
      destinationSelect.selectedIndex = 0
    }
    
    // Set input type to "tons" (default)
    const inputTypeSelect = newRow.querySelector('select[name*="[input_type]"]')
    if (inputTypeSelect) {
      inputTypeSelect.value = 'tons'
    }
    
    // Reset other form values (input_value, notes)
    this.resetFormFields(newRow)
    
    // Make row visible (remove hidden class if present)
    newRow.classList.remove('hidden')
    
    // Append the new row to the rows container
    rowsContainer.appendChild(newRow)
    
    // Trigger Stimulus to recognize the new elements
    // This ensures data-action attributes work on cloned elements
    if (window.Stimulus) {
      window.Stimulus.load(newRow)
    }

    if (card) this.updateRunningAvailabilityForGroup(card)
  }

  removeRow(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const container = this.containerTarget
    if (!container) return
    
    // Count existing rows (excluding template rows)
    const existingRows = container.querySelectorAll(".movement-row:not(.movement-row-template .movement-row)")
    
    // Don't allow removing if it's the last row (optional: can be removed)
    // if (existingRows.length <= 1) {
    //   return
    // }
    
    // Try multiple ways to find the row
    let row = button.closest(".movement-row")
    if (!row) {
      // If closest doesn't work, try parent traversal
      let parent = button.parentElement
      while (parent && !parent.classList.contains("movement-row")) {
        parent = parent.parentElement
      }
      row = parent
    }
    if (row) {
      const card = row.closest(".movement-group-card")
      row.remove()
      if (card) this.updateRunningAvailabilityForGroup(card)
    }
  }

  // Toggles the visibility of the exclusion reason area and shows an
  // informational warning when the checkbox is checked and the row
  // already contains user-entered data.
  toggleExclusionReason(event) {
    const checkbox = event.currentTarget
    const row = checkbox.closest("[data-movement-rows-target='row']") || checkbox.closest(".movement-row")
    if (!row) return

    const reason = row.querySelector("[data-movement-rows-target='exclusionReason']")
    const warning = row.querySelector("[data-movement-rows-target='exclusionWarning']")
    if (!reason) return

    if (checkbox.checked) {
      reason.style.display = ""

      if (warning) {
        const hasData = this.rowHasData(row)
        warning.classList.toggle("hidden", !hasData)
      }
    } else {
      reason.style.display = "none"
      if (warning) {
        warning.classList.add("hidden")
      }
    }
  }

  updateFieldNames(row, newIndex) {
    const fields = row.querySelectorAll("input, select, label")
    fields.forEach((field) => {
      if (field.name) {
        // Replace TEMPLATE_INDEX or movement index
        field.name = field.name.replace(
          /movements\[TEMPLATE_INDEX\]|movements\[\d+\]/,
          `movements[${newIndex}]`
        )
        // Update movement_destinations_attributes index to 0 for new row
        field.name = field.name.replace(
          /movement_destinations_attributes\[\d+\]/,
          "movement_destinations_attributes[0]"
        )
      }
      if (field.id) {
        // Update ID to match new index
        field.id = field.id.replace(/movements\[TEMPLATE_INDEX\]|movements\[\d+\]/, `movements[${newIndex}]`)
        field.id = field.id.replace(/movement_destinations_attributes\[\d+\]/, "movement_destinations_attributes[0]")
      }
      if (field.tagName === "LABEL" && field.getAttribute("for")) {
        // Update label for attribute
        const forAttr = field.getAttribute("for")
        field.setAttribute("for", forAttr.replace(/movements\[TEMPLATE_INDEX\]|movements\[\d+\]/, `movements[${newIndex}]`))
      }
    })
  }

  resetFormFields(row) {
    // Reset input_value and notes, but keep selects as they are already set
    const inputValue = row.querySelector('input[name*="[input_value]"]')
    if (inputValue) {
      inputValue.value = ""
    }

    const notes = row.querySelector('input[name*="[notes]"]')
    if (notes) {
      notes.value = ""
    }

    // CML is cleared and hidden until user selects an appropriate zone pair
    const cmlInput = row.querySelector("[data-cml-input]")
    if (cmlInput) {
      cmlInput.value = ""
    }
    const cmlContainer = row.querySelector("[data-cml-container]")
    if (cmlContainer) {
      cmlContainer.hidden = true
    }
  }

  // Returns true if the given row contains meaningful user-entered data
  // (currently checks input_value and notes fields).
  rowHasData(row) {
    const inputValue = row.querySelector('input[name*="[input_value]"]')
    const notes = row.querySelector('input[name*="[notes]"]')

    const hasInputValue = inputValue && inputValue.value.trim() !== ""
    const hasNotes = notes && notes.value.trim() !== ""

    return hasInputValue || hasNotes
  }

  // === Availability helpers ===

  handleOriginChange(event) {
    const select = event.currentTarget
    const row = select.closest("[data-movement-rows-target='row']") || select.closest(".movement-row")
    if (row && row.dataset) delete row.dataset.originInventoryBeforeMovement
    const card = row?.closest(".movement-group-card")
    if (card) this.updateRunningAvailabilityForGroup(card)
    if (row) this.updateCmlVisibility(row)
  }

  // Show/hide the CML input for a row based on whether the (origin_zone, destination_zone) pair
  // corresponds to an AvailableMovement that requires a processing batch code.
  // When hidden, the input value is cleared to avoid stale CML being submitted.
  updateCmlVisibility(row) {
    const cmlContainer = row.querySelector("[data-cml-container]")
    if (!cmlContainer) return

    const originSelect = row.querySelector('select[name*="[origin_zone_id]"]')
    const destSelect = row.querySelector('select[name*="[destination_zone_id]"]')
    const originZoneId = originSelect?.value?.toString()
    const destZoneId = destSelect?.value?.toString()

    if (!originZoneId || !destZoneId) {
      cmlContainer.hidden = true
      return
    }

    const zoneGroupMap = this.hasZoneGroupByZoneValue ? this.zoneGroupByZoneValue : {}
    const cmlPairs = this.hasRequiresCmlZoneGroupPairsValue ? this.requiresCmlZoneGroupPairsValue : []

    const originGroupId = zoneGroupMap[originZoneId]?.toString()
    const destGroupId = zoneGroupMap[destZoneId]?.toString()

    const needsCml = originGroupId && destGroupId &&
      cmlPairs.some(([s, d]) => s.toString() === originGroupId && d.toString() === destGroupId)

    cmlContainer.hidden = !needsCml
    if (!needsCml) {
      const cmlInput = cmlContainer.querySelector("[data-cml-input]")
      if (cmlInput) cmlInput.value = ""
    }
  }

  updateAllOriginAvailabilityLabels() {
    if (!this.hasContainerTarget) return

    this.containerTarget.querySelectorAll(".movement-group-card").forEach((card) => {
      this.updateRunningAvailabilityForGroup(card)
    })
  }

  // Initialise CML field visibility for all existing rows on page load.
  updateAllCmlVisibility() {
    if (!this.hasContainerTarget) return

    this.containerTarget
      .querySelectorAll("[data-movement-rows-target='row']")
      .forEach((row) => this.updateCmlVisibility(row))
  }

  /** Quantità in tonnellate dalla riga (solo input_type tons; altri tipi = 0 per il saldo nel form). */
  tonneQuantityFromRow(row) {
    const inputType = row.querySelector('select[name*="[input_type]"]')?.value
    if (inputType !== "tons") return 0
    const raw = row.querySelector('input[name*="[input_value]"]')?.value
    if (!raw || String(raw).trim() === "") return 0
    const normalized = String(raw).replace(",", ".")
    const n = parseFloat(normalized)
    return Number.isNaN(n) ? 0 : n
  }

  /** Prima occorrenza di una zona nel gruppo: preciso (movimento salvato) o giacenza a inizio giornata −1s. */
  resolveInitialBeforeForRow(row, zoneId, selectedOption) {
    const precise = row.dataset?.originInventoryBeforeMovement
    if (precise !== undefined && precise !== "") {
      const p = parseFloat(precise)
      if (!Number.isNaN(p)) return p
    }

    let optBefore = selectedOption?.dataset?.inventoryBeforeDay
    if (optBefore === undefined || optBefore === null || optBefore === "") {
      optBefore = undefined
    }
    if (
      (optBefore === undefined || optBefore === null || optBefore === "") &&
      this.hasOriginInventoriesBeforeDayValue
    ) {
      const invB = this.originInventoriesBeforeDayValue || {}
      let q = invB[zoneId]
      if (q === undefined) {
        const numericId = parseInt(zoneId, 10)
        if (!Number.isNaN(numericId)) q = invB[numericId]
      }
      return q
    }
    return optBefore
  }

  /**
   * Aggiorna etichette EOD + "prima del movimento" con saldo progressivo tra righe
   * consecutive con la stessa zona origine (le righe non salvate non sono nel DB).
   */
  updateRunningAvailabilityForGroup(card) {
    const rowsContainer = card.querySelector(".movement-rows-container")
    if (!rowsContainer) return

    const rows = [...rowsContainer.children].filter((el) => el.classList.contains("movement-row"))
    const running = Object.create(null)
    const labelEod = this.hasLabelEodValue ? this.labelEodValue : ""
    const labelBefore = this.hasLabelBeforeValue ? this.labelBeforeValue : ""

    rows.forEach((row) => {
      const originSelect = row.querySelector('select[name*="[origin_zone_id]"]')
      const eodEl =
        row.querySelector("[data-movement-rows-target='originAvailabilityEod']") ||
        row.querySelector("[data-movement-rows-target='originAvailability']")
      const beforeEl = row.querySelector("[data-movement-rows-target='originAvailabilityBefore']")

      if (!originSelect || !eodEl) return

      const zoneId = originSelect.value
      if (!zoneId) {
        eodEl.classList.add("hidden")
        eodEl.textContent = ""
        if (beforeEl) {
          beforeEl.classList.add("hidden")
          beforeEl.textContent = ""
        }
        return
      }

      const selectedOption = originSelect.options[originSelect.selectedIndex]

      // --- Fine giornata (stessa per tutte le righe della zona) ---
      let eodQty = selectedOption?.dataset?.inventory
      if ((eodQty === undefined || eodQty === null || eodQty === "") && this.hasOriginInventoriesValue) {
        const inventories = this.originInventoriesValue || {}
        eodQty = inventories[zoneId]
        if (eodQty === undefined) {
          const numericId = parseInt(zoneId, 10)
          if (!Number.isNaN(numericId)) {
            eodQty = inventories[numericId]
          }
        }
      }

      if (eodQty === undefined || eodQty === null || eodQty === "") {
        eodEl.classList.add("hidden")
        eodEl.textContent = ""
      } else {
        const n = parseFloat(eodQty)
        const formatted = Number.isNaN(n) ? eodQty : n.toFixed(1)
        eodEl.classList.remove("hidden")
        eodEl.textContent = labelEod ? `${labelEod}: ${formatted} ton` : `Disponibile: ${formatted} ton`
      }

      if (!beforeEl) return

      const zKey = String(zoneId)
      if (!(zKey in running)) {
        running[zKey] = this.resolveInitialBeforeForRow(row, zoneId, selectedOption)
      }

      const displayBefore = running[zKey]
      const qty = this.tonneQuantityFromRow(row)

      if (displayBefore === undefined || displayBefore === null || displayBefore === "") {
        beforeEl.classList.add("hidden")
        beforeEl.textContent = ""
      } else {
        const nb = parseFloat(displayBefore)
        const formattedBefore = Number.isNaN(nb) ? displayBefore : nb.toFixed(1)
        beforeEl.classList.remove("hidden")
        beforeEl.textContent = labelBefore
          ? `${labelBefore}: ${formattedBefore} ton`
          : `Disponibile: ${formattedBefore} ton`
      }

      const cur = parseFloat(running[zKey])
      if (!Number.isNaN(cur)) {
        running[zKey] = Math.max(0, cur - qty)
      }
    })
  }
}
